import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDisclosure } from '@chakra-ui/react'
import {
  TransactionReceipt,
  TransactionResponse,
} from '@ethersproject/providers'
import { useWeb3React } from '@web3-react/core'
import { useSteps } from 'chakra-ui-steps'
import { ConfirmationStep, ITransaction, TransactionStatus } from 'types'

import ConfirmationWithSteps from 'components/Modals/ConfirmationWithSteps'
import { useTransactionToast } from 'components/Toast'

import { usePlatformContext } from './PlatformContext'

interface ITransactionContext {
  transactions: ITransaction[]
  addTransaction: (name: string, txHash: string) => void
  updateTransaction: (
    _txHash: string,
    _isConfirmed?: boolean,
    _showToast?: boolean
  ) => void
  clearTransactions: () => void
  openConfirmationWithSteps: (
    steps?: ConfirmationStep[],
    reset?: boolean
  ) => void
  isConfirmationWithStepsAvailable: boolean
  closeConfirmationWithSteps: (resetSteps?: boolean) => void
  setStep: (step: number) => void
  reset: () => void
  setErrorMsg: (msg: string) => void
}

const defaultState = {
  transactions: [],
  addTransaction: () => null,
  updateTransaction: () => null,
  clearTransactions: () => true,
  openConfirmationWithSteps: () => null,
  isConfirmationWithStepsAvailable: false,
  closeConfirmationWithSteps: () => null,
  setStep: () => null,
  reset: () => null,
  setErrorMsg: () => null,
}

const TransactionContext = createContext<ITransactionContext>(defaultState)
interface ITransactionProvider {
  children: ReactNode
}

export const TransactionProvider: FC<ITransactionProvider> = ({ children }) => {
  const { platformChainId } = usePlatformContext()
  const { provider, account } = useWeb3React()
  const [_oldTxs, setOldTxs] = useState<ITransaction[]>([])
  const [transactions, setTransactions] = useState<ITransaction[]>(
    defaultState.transactions
  )
  const txToast = useTransactionToast()
  // For the ConfirmationWithSteps
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { setStep, reset, activeStep } = useSteps({
    initialStep: 0,
  })
  const [steps, setSteps] = useState<ConfirmationStep[]>([])
  const [errorMsg, setErrorMsg] = useState<string>('')
  //

  const checkPendingTxs = async (pendingTxs: ITransaction[]) => {
    // console.log('Checking ??')
    if (!provider) return pendingTxs
    // console.log('Checking pending txs!')
    const newPendingTxs = await Promise.all(
      pendingTxs.map((tx: ITransaction) =>
        provider
          .getTransactionReceipt(tx.txHash)
          .then((receipt: TransactionReceipt | null) => {
            if (!receipt) {
              // console.log(tx.txHash, 'tx is still pending or not mined!')
              // Check if the tx is pending or replaced:
              return provider
                .getTransaction(tx.txHash)
                .then((transaction: TransactionResponse) => {
                  // console.log('getTransaction response:', transaction)
                  if (transaction) {
                    return tx
                  }
                  return {
                    ...tx,
                    status: TransactionStatus.dropped,
                  }
                })
                .catch((err) => {
                  console.log(
                    'err on getting the TransactionResponse of ',
                    tx.txHash,
                    err
                  )
                  return tx
                })
            } else if (receipt.status === 1) {
              // console.log(tx.txHash, 'tx was confirmed!')
              return {
                ...tx,
                status: TransactionStatus.confirmed,
                confirmedTime: Date.now(),
              }
            } else {
              // console.log(tx.txHash, 'tx was reverted!')
              return {
                ...tx,
                status: TransactionStatus.failed,
                confirmedTime: Date.now(),
              }
            }
          })
          .catch((err) => {
            console.log('err on getting TXReceipt of ', tx.txHash, err)
            return tx
          })
      )
    )
    // console.log('newPendingTxs', newPendingTxs)
    setTransactions((oldTransactions) => {
      const newTransactions = oldTransactions.map((x) => {
        const foundPendingTx = newPendingTxs.find((y) => y.txHash === x.txHash)
        return foundPendingTx || x
      })
      if (window && window.localStorage) {
        // console.log('saving txs on local')
        window.localStorage.setItem(
          'transactions',
          JSON.stringify([..._oldTxs, ...newTransactions])
        )
      }
      return newTransactions
    })
  }

  const loadAccountTxs = async (_account: string, _chainId: number) => {
    const lsTransactions = window.localStorage.getItem('transactions')
    if (lsTransactions) {
      // Load here relevant transactions of connected user in connected network chainId
      const _: ITransaction[] = JSON.parse(lsTransactions)
      const _t = []
      const _o = []
      for (let i = 0; i < _.length; i++) {
        if (_[i].from === _account && _[i].chainId === _chainId) {
          _t.push(_[i])
        } else {
          _o.push(_[i])
        }
      }
      const pendingUserTxs = _t.filter(
        (x) => x.status === TransactionStatus.pending
      )
      // console.log('Pending txs for the user are:', pendingUserTxs)
      setTransactions(_t)
      setOldTxs(_o)
      checkPendingTxs(pendingUserTxs)
    }
  }

  useEffect(() => {
    if (account && platformChainId) {
      if (window && window.localStorage) {
        loadAccountTxs(account, platformChainId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, platformChainId])

  const clearTransactions = () => {
    if (window && window.localStorage) {
      // console.log('saving txs on local')
      window.localStorage.setItem('transactions', JSON.stringify(_oldTxs))
    }
    setTransactions([])
  }

  const addTransaction = (name: string, txHash: string) => {
    const newTransaction = {
      txHash,
      name,
      status: TransactionStatus.pending,
      chainId: platformChainId,
      from: account || '',
      addedTime: Date.now(),
    }
    if (window && window.localStorage) {
      // console.log('saving txs on local')
      window.localStorage.setItem(
        'transactions',
        JSON.stringify([..._oldTxs, ...transactions, newTransaction])
      )
    }
    setTransactions((oldTransactions) => [...oldTransactions, newTransaction])
  }

  const updateTransaction = (
    txHash: string,
    isConfirmed = true,
    showToast = false
  ) => {
    setTransactions((oldTransactions) => {
      // console.log('Updating tx:', txHash, isConfirmed)
      // console.log('old txs', oldTransactions)
      const updatedTransactions = oldTransactions.map((x: ITransaction) => {
        if (x.txHash === txHash) {
          if (showToast) {
            txToast({
              title: x.name,
              txHash: x.txHash,
              chainId: platformChainId,
              duration: 15000,
              txStatus: isConfirmed
                ? TransactionStatus.confirmed
                : TransactionStatus.failed,
            })
          }
          return {
            ...x,
            status: isConfirmed
              ? TransactionStatus.confirmed
              : TransactionStatus.failed,
            confirmedTime: isConfirmed ? Date.now() : undefined,
          }
        }

        return x
      })
      if (window && window.localStorage) {
        // console.log('saving txs on local')
        window.localStorage.setItem(
          'transactions',
          JSON.stringify([..._oldTxs, ...updatedTransactions])
        )
      }
      return updatedTransactions
    })
  }

  const closeConfirmationWithSteps = (resetSteps = true) => {
    onClose()
    if (resetSteps) {
      reset()
      setErrorMsg('')
      setSteps([])
    }
  }

  const openConfirmationWithSteps = (
    steps: ConfirmationStep[] | undefined,
    resetSteps = true
  ) => {
    if (steps) {
      if (resetSteps) {
        reset()
      }
      setErrorMsg('')
      setSteps(steps)
    }
    onOpen()
  }

  const isConfirmationWithStepsAvailable = useMemo(() => {
    return steps.length > 0
  }, [steps])

  const _setSteps = (_step: number) => {
    setStep(_step)
    onOpen()
  }

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        addTransaction,
        updateTransaction,
        clearTransactions,
        openConfirmationWithSteps,
        isConfirmationWithStepsAvailable,
        closeConfirmationWithSteps,
        setStep: _setSteps,
        reset,
        setErrorMsg,
      }}
    >
      <>
        {children}
        <ConfirmationWithSteps
          isOpen={isOpen}
          onClose={closeConfirmationWithSteps}
          errorMsg={errorMsg}
          steps={steps}
          activeStep={activeStep}
        />
      </>
    </TransactionContext.Provider>
  )
}

export const useTransactionContext = () =>
  useContext(TransactionContext) as ITransactionContext

export default TransactionContext
